import StickyTableResponsive from '../../../components/common/StickyTableResponsive';
import SortableTh from '../../../components/common/SortableTh';
import React, { useCallback, useEffect, useState } from 'react';
import { useTableSort } from '../../../hooks/useTableSort';
import { Link } from 'react-router-dom';
import MasterLayout from '../../../masterLayout/MasterLayout';
import Breadcrumb from '../../../components/Breadcrumb';
import {
  AppButton,
  AppEmptyState,
  AppSelect,
  AppSpinner,
} from '../../../components/ui';
import KkpColorBadge from '../../../components/kkp/KkpColorBadge';
import { AppFilterBar, AppFilterInput, AppFilterSelect, AppFilterCommercialSelect } from '../../../components/filters';
import secteursAdminService from '../../../services/secteursAdminService';
import { ROUTES } from '../../../config/routes';
import { SOCIETE_BRAND } from '../../../config/societeBrand';
import { filterRowsByMulti, singleOrEmpty } from '../../../utils/kkpPlanningFilters';
import { notify } from '../../../components/common/AppToast';
import '../../../styles/kkp-planning-page.scss';

const ProduitList = ({ produits }) => {
  if (!produits?.length) return <span className="text-muted">—</span>;
  return (
    <ul className="list-unstyled mb-0">
      {produits.map((p, i) => (
        <li key={i}>
          <small>
            {p.label}
            {p.ref && <span className="text-muted"> ({p.ref})</span>}
            {p.origine === 'facture' && <span className="badge bg-success-600 ms-1" title="Facture">F</span>}
            {p.origine === 'commande' && <span className="badge bg-warning text-dark ms-1" title="Commande">C</span>}
          </small>
        </li>
      ))}
    </ul>
  );
};

const TABLE_SORT_COLUMNS = {
  entreprise: { type: 'string', getValue: (row) => row.entreprise_nom },
  commercial: { type: 'string', getValue: (row) => row.dernier_vendeur },
  iticonseil: { type: 'string', getValue: (row) => (row.iticonseil || []).map((p) => p.label).join(', ') },
  koikispass: { type: 'string', getValue: (row) => (row.koikispass || []).map((p) => p.label).join(', ') },
  autre: { type: 'string', getValue: (row) => (row.autre || []).map((p) => p.label).join(', ') },
};

const SecteursAdminEntreprisesProduitsPage = () => {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [entreprises, setEntreprises] = useState([]);
  const [filtres, setFiltres] = useState({ produits: [], vendeurs: [] });
  const [filtresLoading, setFiltresLoading] = useState(true);
  const [filtresEndpointAvailable, setFiltresEndpointAvailable] = useState(true);
  const [periodeLabel, setPeriodeLabel] = useState('');
  const [filters, setFilters] = useState({ client: '', produit: [], societe: [], vendeur: [] });
  const [loading, setLoading] = useState(true);

  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(entreprises, TABLE_SORT_COLUMNS);

  const applyFiltresFromResponse = useCallback((data) => {
    if (!data) return;
    if (data.filtres) {
      setFiltres({
        produits: data.filtres.produits ?? [],
        vendeurs: data.filtres.vendeurs ?? [],
      });
    } else if (data.produits || data.vendeurs) {
      setFiltres({
        produits: data.produits ?? [],
        vendeurs: data.vendeurs ?? [],
      });
    }
    if (data.periode_label) setPeriodeLabel(data.periode_label);
  }, []);

  const toApiFilters = useCallback((f) => ({
    client: f.client || '',
    produit: singleOrEmpty(f.produit),
    societe: singleOrEmpty(f.societe),
    vendeur: singleOrEmpty(f.vendeur),
  }), []);

  const applyClientMultiFilters = useCallback((rows, f) => {
    let next = rows;
    next = filterRowsByMulti(next, f.vendeur, (row) => row.dernier_vendeur);
    if (Array.isArray(f.societe) && f.societe.length > 1) {
      next = next.filter((row) => f.societe.some((s) => {
        if (s === 'iticonseil') return (row.iticonseil || []).length > 0;
        if (s === 'koikispass') return (row.koikispass || []).length > 0;
        if (s === 'autre') return (row.autre || []).length > 0;
        return false;
      }));
    }
    if (f.produit.length > 1) {
      const set = new Set(f.produit.map(String));
      next = next.filter((row) => {
        const all = [...(row.iticonseil || []), ...(row.koikispass || []), ...(row.autre || [])];
        return all.some((p) => set.has(String(p.id || p.ref || p.label)));
      });
    }
    return next;
  }, []);

  const load = useCallback(async (f = filters, forceIncludeFiltres = null) => {
    setLoading(true);
    try {
      const data = await secteursAdminService.getEntreprisesProduits({
        annee,
        ...toApiFilters(f),
        includeFiltres: forceIncludeFiltres ?? !filtresEndpointAvailable,
      });
      setEntreprises(applyClientMultiFilters(data.entreprises ?? [], f));
      applyFiltresFromResponse(data);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [annee, filters, filtresEndpointAvailable, applyFiltresFromResponse, toApiFilters, applyClientMultiFilters]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setFiltresLoading(true);
      try {
        const filtresData = await secteursAdminService.getEntreprisesProduitsFiltres(annee);
        if (cancelled) return;
        if (filtresData) {
          setFiltresEndpointAvailable(true);
          applyFiltresFromResponse(filtresData);
        } else {
          setFiltresEndpointAvailable(false);
        }
      } catch (err) {
        if (!cancelled) notify.error(err.message);
      } finally {
        if (!cancelled) setFiltresLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [annee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (filtresLoading) return undefined;
    const timer = setTimeout(() => load(filters), 300);
    return () => clearTimeout(timer);
  }, [annee, filters.client, filters.produit, filters.societe, filters.vendeur, filtresLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReset = () => {
    setFilters({ client: '', produit: [], societe: [], vendeur: [] });
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Entreprises et produits"
        subtitle={periodeLabel}
        actions={(
          <div className="d-flex gap-2 align-items-center">
            <AppButton as={Link} to={ROUTES.secteurs.admin} size="sm" variant="outline-secondary">Retour</AppButton>
            <AppSelect
              size="sm"
              className="w-auto"
              value={annee}
              onChange={(e) => setAnnee(Number(e.target.value))}
            >
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </AppSelect>
          </div>
        )}
      />

      <AppFilterBar
        onReset={handleReset}
        resetDisabled={!filters.client && !filters.produit.length && !filters.societe.length && !filters.vendeur.length}
      >
        <AppFilterInput
          id="sep-client"
          label="Client"
          placeholder="Nom ou ID…"
          value={filters.client}
          onChange={(client) => setFilters((f) => ({ ...f, client }))}
        />
        <AppFilterSelect
          id="sep-produit"
          label="Produit"
          value={filters.produit}
          onChange={(produit) => setFilters((f) => ({ ...f, produit }))}
          disabled={filtresLoading}
          multiple={false}
          placeholder={filtresLoading ? 'Chargement…' : 'Tous les produits'}
          options={(filtres.produits || []).map((p) => {
            const value = String(p.id || p.ref || p.label);
            return { value, label: `${p.label}${p.ref ? ` (${p.ref})` : ''}` };
          })}
        />
        <AppFilterSelect
          id="sep-societe"
          label="Société"
          value={filters.societe}
          onChange={(societe) => setFilters((f) => ({ ...f, societe }))}
          multiple={false}
          options={[
            { value: 'iticonseil', label: 'Iticonseil' },
            { value: 'koikispass', label: 'Koikispass' },
            { value: 'autre', label: 'Autre' },
          ]}
        />
        <AppFilterCommercialSelect
          id="sep-vendeur"
          value={filters.vendeur}
          onChange={(vendeur) => setFilters((f) => ({ ...f, vendeur }))}
          disabled={filtresLoading}
          placeholder={filtresLoading ? 'Chargement…' : '—'}
          options={(filtres.vendeurs || []).map((v) => ({ value: v.nom, label: v.nom }))}
        />
      </AppFilterBar>

      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <AppSpinner />
          ) : entreprises.length === 0 ? (
            <AppEmptyState message="Aucune entreprise trouvée" />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <SortableTh sortKey="entreprise" label="Entreprise" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="iticonseil" label="Iticonseil" className={SOCIETE_BRAND.iticonseil.cellClass} sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="koikispass" label="Koikispass" className={SOCIETE_BRAND.koikispass.cellClass} sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="autre" label="Autre" className="bg-secondary-50" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((e, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{e.entreprise_nom}</strong>
                        <br /><small className="text-muted">ID: {e.entreprise_socid}</small>
                      </td>
                      <td>
                        {e.dernier_vendeur ? (
                          <KkpColorBadge color={e.dernier_vendeur_couleur} vivid>
                            {e.dernier_vendeur}
                          </KkpColorBadge>
                        ) : '—'}
                      </td>
                      <td className={SOCIETE_BRAND.iticonseil.cellClass}><ProduitList produits={e.iticonseil} /></td>
                      <td className={SOCIETE_BRAND.koikispass.cellClass}><ProduitList produits={e.koikispass} /></td>
                      <td className="bg-light"><ProduitList produits={e.autre} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
      </div>
    </MasterLayout>
  );
};

export default SecteursAdminEntreprisesProduitsPage;
